// cron 校验规则
// Cron表达式由6个字段组成，依次表示秒、分钟、小时、日、月份、星期，每个字段之间用空格分隔。

// 符号：
// * 表示匹配任意值。例如，小时字段设置为*，表示匹配任意小时。
// / 表示步长。例如，分钟字段设置为*/5，表示每隔5分钟执行一次任务。
// , 表示枚举多个值。例如，星期字段设置为1,3,5，表示任务会在星期一、三、五执行。
// — 表示区间。例如，日字段设置为10—15，表示任务会在每个月的10号到15号这个区间内执行。
// ? 仅适用于星期和日字段，表示不指定值。例如，星期字段设置为？，表示星期几不作为匹配条件。星期和日字段不能同时指定具体的值，如果一个字段指定了具体的值，另一个字段必须设置为?。
// # 仅适用于星期字段，表示第几个星期几。例如，星期字段设置为4#2，表示任务会在每个月的第二个星期四执行。

// 字段范围和要求：
// 秒字段（0～59）：不可为空，仅支持数字。
// 分钟字段（0～59）：不可为空，仅支持数字。
// 小时字段（0～23）：不可为空，仅支持数字。
// 日字段（1～31）：不可为空，仅支持数字。
// 月份字段（1～12）：不可为空，仅支持数字。
// 星期字段（0～7或者SUN~SAT，其中0和7都表示星期天）：不可为空，仅支持数字。#后面的数字表示第几个星期几，范围为1~5。

// TODO: 后续如果对接 Quartz Cron，还需要一个范围为 1970-2099 的年字段，目前不支持

function validateFieldFromRegs(desc: string, field: string, regs: RegExp[]) {
  const res = regs.some((reg) => reg.test(field));
  if (!res) throw new Error(`${desc}字段格式不合法`);
}

function validateWeekdayFormat(field: string): void {
  const weekdayAbbrevs = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
  const hasNumbers = /\d/.test(field);
  const hasAbbrevs = weekdayAbbrevs.some(abbrev => field.includes(abbrev));

  if (hasNumbers && hasAbbrevs) {
    throw new Error('星期字段不能混合使用数字和英文缩写，请统一使用数字或英文缩写');
  }
}

function convertWeekdayToNumber(field: string): string {
  const weekdayMap: { [key: string]: string } = {
    'SUN': '0',
    'MON': '1',
    'TUE': '2',
    'WED': '3',
    'THU': '4',
    'FRI': '5',
    'SAT': '6'
  };

  // 如果包含星期几缩写，进行替换
  let result = field;
  for (const [weekday, number] of Object.entries(weekdayMap)) {
    result = result.replace(new RegExp(weekday, 'g'), number);
  }

  return result;
}

function validRange(desc: string, field: string, min: number, max: number, errorMessage?: string) {
  const nums = field.match(/(\d+)/g)?.map((n) => Number.parseInt(n)) || [];

  if ((field.includes("#") || field.includes("-")) && nums.length !== 2) {
    throw new Error("unknown format error");
  }

  if (nums.some(Number.isNaN)) {
    throw new Error("仅支持数字");
  }

  if (!field.includes("#")) {
    for (const num of nums) {
      if (num < min || num > max) {
        throw new Error(errorMessage || `${desc}字段的范围为${min}~${max}`);
      }
    }

    if (field.includes("-") && nums[0]! >= nums[1]!) {
      throw new Error(`${desc}字段范围不合法，前者必须小于后者`);
    }
  } else {
    // 星期字段特殊规则
    if (nums[0]! < 0 || nums[0]! > 7) {
      throw new Error(`星期字段的范围为0~7，其中0和7都表示星期天`);
    }
    if (nums[1]! < 1 || nums[1]! > 5) {
      throw new Error(`星期字段#后面的数字表示第几个星期几，范围为1~5`);
    }
  }
}

export function validateCron(value: string) {
  const vals = value.trim().split(" ");
  if (vals.length !== 6) {
    return "Cron表达式必须包含6个字段";
  }
  const [sec, min, hour, day, month, week] = vals as [string, string, string, string, string, string];

  const convertedWeek = convertWeekdayToNumber(week); // 将星期缩写替换为数字

  const numRegex = /^\d+$/;
  const anyRegex = /^\*$/;
  const stepRegex = /^(\d+|\*)\/(\d+)$/;
  const enumRegex = /^(\d+,)+\d+$/;
  const rangeRegex = /^(\d+)-(\d+)$/;
  const noSpecRegex = /^\?$/;
  const nthWeekdayRegex = /^(\d)#(\d)$/;

  const baseRegs = [numRegex, anyRegex, stepRegex, enumRegex, rangeRegex];

  try {
    // 格式不合法
    validateFieldFromRegs("秒", sec, baseRegs);
    validateFieldFromRegs("分钟", min, baseRegs);
    validateFieldFromRegs("小时", hour, baseRegs);
    validateFieldFromRegs("日", day, [...baseRegs, noSpecRegex]);
    validateFieldFromRegs("月份", month, baseRegs);
    validateWeekdayFormat(week); // 验证星期字段格式（不允许混合使用数字和英文缩写）
    validateFieldFromRegs("星期", convertedWeek, [...baseRegs, noSpecRegex, nthWeekdayRegex]);
    // 范围不合法
    validRange("秒", sec, 0, 59);
    validRange("分钟", min, 0, 59);
    validRange("小时", hour, 0, 23);
    validRange("日", day, 1, 31);
    validRange("月份", month, 1, 12);
    validRange("星期", convertedWeek, 0, 7, '星期字段的范围为0~7，其中0和7都表示星期天');
  } catch (e: Error | any) {
    return e?.message || "unknown error";
  }

  // 特殊规则：星期和日字段不能同时指定具体的值，如果一个字段指定了具体的值，另一个字段必须设置为?
  // if (!noSpecRegex.test(day) && !noSpecRegex.test(convertedWeek)) return "星期和日字段不能同时指定具体的值，如果一个字段指定了具体的值，另一个字段必须设置为?";
  // 特殊规则：星期和日字段必须指定一个具体的值，不能同时为?
  if (noSpecRegex.test(day) && noSpecRegex.test(convertedWeek)) return "星期和日字段必须指定一个具体的值，不能同时为?";

  return true;
}
